<script lang="ts">
	import RoundShare from 'virtual:icons/ic/round-share';
	import bookCoverImg from '@lib/images/placeholders/book-cover.webp?enhanced';
	import { page } from '$app/stores';
	import { goto } from '$app/navigation';
	import { getDiscountPercentage, priceFormatter } from '@lib/helpers/global';
	import { PUBLIC_API_URL, PUBLIC_BRAND_CODE } from '$env/static/public';
	import { toast } from 'svelte-sonner';
	import { formatPriceForDisplay } from '@lib/utils';
	import ChaptersProgress from './ChaptersProgress.svelte';
	import Modal from './Modal.svelte';
	import SharePanel from '../app/books/SharePanel.svelte';
	import type { BookData, DraftBookData, SearchItem } from '@lib/types';

	export let viewDirection: 'horizontal' | 'vertical' = 'vertical',
		book: BookData | DraftBookData,
		isWithPriceTag = true,
		type: 'published' | 'draft' = 'published',
		bookUrl = new URL(`/books/${book.slug}`, $page.url.origin),
		disabled = false;

	const { userToken } = $page.data;

	function gotoBookDetail() {
		const currentUrl = $page.url;
		const backUrl = currentUrl.searchParams.get('back_url');

		if (backUrl) currentUrl.searchParams.delete('back_url');

		bookUrl.searchParams.set('back_url', currentUrl.href);
		goto(bookUrl)
			.then(async () => await setLastViewBook())
			.catch((error) => {
				console.error(error);
				toast.error('Oops, terjadi kesalahan');
			});
	}

	let sortedPriceSubscriptions: BookData['SubscriptionPlan'] = [],
		shareModalEl: HTMLDialogElement;

	$: if (type === 'published') {
		bookUrl = new URL(`/books/${book.slug}`, $page.url.origin);
	} else {
		bookUrl = new URL(`/book-publishing/${book.id}/choose-chapter`, $page.url.origin);
	}

	$: if ('SubscriptionPlan' in book)
		sortedPriceSubscriptions = book.SubscriptionPlan?.sort((a, b) => a.price - b.price);

	async function getRecentlyViewed() {
		try {
			const request = await fetch(`${PUBLIC_API_URL}/${PUBLIC_BRAND_CODE}/history`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${userToken}`
				}
			});
			const response = await request.json();
			const {
				meta: { status, message }
			} = await response;

			if (request.ok) {
				const {
					data: { search_items }
				}: {
					data: {
						search_items: SearchItem[];
					};
				} = response;

				return search_items;
			} else {
				console.error(status, message);
			}
		} catch (error) {
			console.error(error);
		}
	}

	async function storeRecentlyViewed() {
		try {
			const request = await fetch(`${PUBLIC_API_URL}/${PUBLIC_BRAND_CODE}/history`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${userToken}`
				},
				body: JSON.stringify({ item_id: book.id })
			});

			const response = await request.json();
			const {
				meta: { status, message }
			}: {
				meta: HTTPMetaResponse;
			} = await response;

			if (!request.ok) console.error(status, message);
		} catch (error) {
			console.error(error);
		}
	}

	async function setLastViewBook() {
		if (userToken) {
			const recentlyViewed = await getRecentlyViewed();

			if (recentlyViewed) {
				const isIncludedRecentlyViewed = recentlyViewed.some((item) => item.id === book.id);
				if (!isIncludedRecentlyViewed) await storeRecentlyViewed();
			}
		}
	}

	$: getShareUrl = (type: 'draft' | 'published') => {
		if (type === 'draft') return `${bookUrl.origin}/book-publishing/${book.id}/choose-chapter`;
		return `${bookUrl.origin}/books/${book.slug}`;
	};

	$: shareUrl = getShareUrl(type);

	function getTextTemplate() {
		if (type === 'draft') {
			return `Yuk, jadi penulis di Buku ${book.title}. Pilih babnya sebelum kuota penuh. ${shareUrl}`;
		}

		if ('description' in book) return `${book.description} ${shareUrl}`;
		return shareUrl;
	}
</script>

<a
	on:click|preventDefault={gotoBookDetail}
	href={bookUrl.href}
	class="flex h-full flex-col items-center gap-4 rounded-lg border bg-base-100 p-3 hover:border-primary"
	class:pointer-events-none={disabled}
	aria-disabled={disabled}
>
	<div
		class="flex size-full items-center gap-4"
		class:flex-row={viewDirection === 'horizontal'}
		class:flex-col={viewDirection === 'vertical'}
	>
		<div class="book-cover" class:max-w-28={viewDirection === 'horizontal'}>
			{#if book.image_url}
				<img loading="lazy" width="117" height="173" src={book.image_url} alt={book.title} />
			{:else}
				<enhanced:img
					class="size-full"
					width="117"
					height="173"
					loading="lazy"
					src={bookCoverImg}
					alt={book.title}
				/>
			{/if}
		</div>
		<div class="flex w-full flex-1 flex-col justify-between">
			<div>
				<p class="line-clamp-2 text-balance text-sm font-medium">
					{book.title}
				</p>
				{#if type === 'draft'}
					<div class="mt-3">
						<p class="text-xs font-medium text-gray-500">Mulai dari</p>
						<div class="mt-1.5 flex items-center gap-1.5">
							<p class="font-semibold text-secondary">{formatPriceForDisplay(book.price)}</p>
							<p class="text-xs font-medium">/bab</p>
						</div>
					</div>
				{/if}
				{#if 'BookWriter' in book}
					<p class="mt-1 line-clamp-1 text-xs text-gray-400">
						{#if book.BookWriter.length}
							{book.BookWriter[0].name}
							{#if book.BookWriter.length > 1}
								(+{book.BookWriter.length - 1})
							{/if}
						{:else}
							Penulis tidak dikenal
						{/if}
					</p>
				{/if}
			</div>
			{#if 'remaining_chapters' in book && 'total_chapters' in book}
				<div class="mt-4 flex items-center gap-1">
					<ChaptersProgress class="flex-1" draftBook={book} />
					<button
						on:click|stopPropagation|preventDefault={() => shareModalEl.showModal()}
						class="grid size-8 place-items-center rounded-full p-1 text-gray-500 hover:bg-base-200"
						type="button"
						aria-label="Bagikan"
					>
						<RoundShare />
					</button>
				</div>
			{/if}
			{#if isWithPriceTag && 'SubscriptionPlan' in book}
				<div id="price-tag-{book.slug}">
					{#if book.SubscriptionPlan}
						{@const [lowestSubscriptionPrice] = sortedPriceSubscriptions}
						{#if lowestSubscriptionPrice}
							{@const { primary_price, price } = lowestSubscriptionPrice}
							<div class="mt-2 flex h-full items-center justify-between space-y-1">
								<div aria-label="Price's stack">
									<p class="mb-1 text-xs text-gray-500">Mulai dari</p>
									{#if primary_price}
										<div class="flex items-center gap-2">
											<p class="text-xs font-medium text-error line-through">
												{priceFormatter.format(primary_price)}
											</p>
											<p
												class="rounded-md bg-success/10 px-1 py-0.5 text-xs font-medium text-success"
											>
												-{getDiscountPercentage(primary_price, price)}%
											</p>
										</div>
									{/if}
									<p class="font-semibold text-secondary">
										{formatPriceForDisplay(price)}
									</p>
								</div>
								<button
									on:click|stopPropagation|preventDefault={() => shareModalEl.showModal()}
									class="grid size-8 place-items-center rounded-full p-1 text-gray-500 hover:bg-base-200"
									type="button"
									aria-label="Bagikan"
								>
									<RoundShare />
								</button>
							</div>
						{/if}
					{/if}
				</div>
			{/if}
		</div>
	</div>
	<slot name="actions" />
</a>

<Modal
	class="cursor-default"
	on:clickoutside={() => shareModalEl.close()}
	bind:modalEl={shareModalEl}
>
	<p class="mb-4 text-lg font-semibold">Bagikan ke Teman-mu!</p>
	<SharePanel {shareUrl} title={book.title} textTemplate={getTextTemplate()}>
		<div slot="image">
			{#if book.image_url}
				<img
					src={book.image_url}
					class="aspect-[131/194] w-full max-w-16 rounded-sm object-cover object-center"
					alt={book.title}
				/>
			{:else}
				<enhanced:img
					src={bookCoverImg}
					class="aspect-[131/194] w-full max-w-16 rounded-sm object-cover object-center"
					loading="lazy"
					alt={book.title}
				/>
			{/if}
		</div>
	</SharePanel>
</Modal>

<style lang="scss">
	.book-cover {
		@apply aspect-[131/194] w-full overflow-hidden rounded-md bg-gray-100;

		& > img {
			@apply aspect-[131/194] size-full object-cover object-center;
		}
	}
</style>
